import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import {
  addEntity,
  removeEntity,
  setAllEntities,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  setError,
  setFulfilled,
  setPending,
  withRequestStatus,
} from '@fsf/util';
import { concatMap, pipe, tap } from 'rxjs';
import {
  NewSavedSimulation,
  SavedSimulation,
} from '../models/saved-simulation.model';
import { SavedSimulationApiRepository } from './saved-simulation.api.repository';

/**
 * The store outlives its consumers, so a plain "last saved set" would stale-select on
 * re-entry. A consumer captures the `seq` present at registration and reacts only to a later
 * one.
 */
export type LastSavedSimulation = { set: SavedSimulation; seq: number };

/**
 * Its own token rather than the shared `requestStatus` reaching `fulfilled`, so a consumer
 * never attributes a concurrent save or load fulfilment to the delete.
 */
export type LastDeletedSimulation = { id: string; seq: number };

type SavedSimulationState = {
  selectedId: string | null;
  lastSaved: LastSavedSimulation | null;
  lastDeleted: LastDeletedSimulation | null;
  loaded: boolean;
  /** The site whose sets are currently held; null before the first load. */
  currentSiteId: string | null;
};

const initialState: SavedSimulationState = {
  selectedId: null,
  lastSaved: null,
  lastDeleted: null,
  loaded: false,
  currentSiteId: null,
};

/**
 * Goes through `SavedSimulationApiRepository`, NOT the root `LaravelApi` — that one is already
 * bound to `/api/candidate-sites`. The repository parses at the boundary, so the store does no
 * parsing of its own.
 *
 * The list is site-scoped and there is no init auto-load; a consumer drives it through
 * `loadForSite`. Mutations are NOT optimistic: entity state is patched only inside each
 * `rxMethod`'s success handler.
 */
export const SavedSimulationStore = signalStore(
  { providedIn: 'root' },
  withEntities<SavedSimulation>(),
  withRequestStatus,
  withState(initialState),
  withMethods((store) => {
    const repo = inject(SavedSimulationApiRepository);
    // Unexposed: `loadForSite` and `reloadCurrentSite` own the `currentSiteId`/`loaded`
    // lifecycle, so a bare refetch cannot desync them.
    const readMany = rxMethod<string>(
      pipe(
        tap(() => patchState(store, setPending())),
        concatMap((siteId) =>
          repo.readMany(siteId).pipe(
            tapResponse({
              // Ignore a response for a site the store has moved off of: switches queue
              // under concatMap and HTTP is not cancelled, so an earlier site's response can
              // land after `currentSiteId` advanced and show the prior site's sets.
              next: (sets) => {
                if (store.currentSiteId() !== siteId) {
                  return;
                }
                patchState(store, setAllEntities(sets), setFulfilled());
              },
              error: (error: unknown) => {
                if (store.currentSiteId() !== siteId) {
                  return;
                }
                patchState(store, setError(error));
              },
              // A superseded fetch must not latch `loaded` for the site that replaced it.
              finalize: () => {
                if (store.currentSiteId() !== siteId) {
                  return;
                }
                patchState(store, { loaded: true });
              },
            }),
          ),
        ),
      ),
    );
    return {
      /**
       * Re-entering the already-current site is a no-op, so switching tabs back does not
       * refetch; a site change always reloads.
       */
      loadForSite(siteId: string): void {
        if (store.currentSiteId() === siteId) {
          return;
        }
        patchState(store, setAllEntities([] as SavedSimulation[]), {
          currentSiteId: siteId,
          selectedId: null,
          loaded: false,
        });
        readMany(siteId);
      },
      /**
       * Bypasses the same-site guard so a failed initial load can be retried without a site
       * change. A no-op before any site has been loaded.
       */
      reloadCurrentSite(): void {
        const siteId = store.currentSiteId();
        if (siteId == null) {
          return;
        }
        patchState(store, { loaded: false });
        readMany(siteId);
      },
      addOne: rxMethod<NewSavedSimulation>(
        pipe(
          tap(() => patchState(store, setPending())),
          concatMap((input) =>
            repo.addOne(input).pipe(
              tapResponse({
                // Insert only after the server confirms, so a failed POST changes nothing.
                next: (set) =>
                  patchState(
                    store,
                    addEntity(set),
                    (state) => ({
                      lastSaved: { set, seq: (state.lastSaved?.seq ?? 0) + 1 },
                    }),
                    setFulfilled(),
                  ),
                error: (error: unknown) => patchState(store, setError(error)),
              }),
            ),
          ),
        ),
      ),
      removeOne: rxMethod<string>(
        pipe(
          tap(() => patchState(store, setPending())),
          concatMap((id) =>
            repo.removeOne(id).pipe(
              tapResponse({
                // Drop only after the server confirms; a failed DELETE keeps the set.
                next: () =>
                  patchState(
                    store,
                    removeEntity(id),
                    (state) => ({
                      lastDeleted: {
                        id,
                        seq: (state.lastDeleted?.seq ?? 0) + 1,
                      },
                    }),
                    setFulfilled(),
                  ),
                error: (error: unknown) => patchState(store, setError(error)),
              }),
            ),
          ),
        ),
      ),
      select(set: SavedSimulation | null): void {
        patchState(store, { selectedId: set?.id ?? null });
      },
      selectById(id: string): void {
        patchState(store, { selectedId: id });
      },
    };
  }),
);
